import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';

export interface ICheckRegisterGenerateParams {
    periodNo: number;
    beginningCheckNumber: number;
    checkDate: string;
    type: string;
    tsdNoList?: string;
    office: string;
    overrideDirectDeposit: string;
}

interface ICheckRegisterGenerateDialogProps {
    dialogName: string;
    hideDialog: () => void;
    onGenerate?: (params: ICheckRegisterGenerateParams) => void;
    // Owned by the parent - mirrors :check_parms.msg, populated once the parent has actually
    // tried the generate call (success count, or CheckForApproval's blocking alert text).
    message?: string;
    // Owned by the parent, fetched at the moment the "Generate" button is clicked (not on this
    // dialog's own mount) - Dialog's CSSTransition unmounts/remounts the actual <input> on every
    // open/close, so a fetch-and-setValue done here on mount runs once at page load, long before
    // the input exists, and gets wiped the moment the real input mounts.
    nextCheckNo?: number;
}

// Mirrors the Check_Parms block (opened by the main screen's "Generate" button) - sets up and
// kicks off a check/direct-deposit run against a pay period. "Type" is check_parms.check_type -
// despite the misleading name (and the field's own earlier iteration on this port that assumed
// otherwise) it's a person-type filter against LM.TYPE (Contractor/Employee), defaulting to
// Contractor same as Oracle - NOT a check-vs-direct-deposit selector. Check vs. Direct Deposit
// is decided per-landman from each landman's own LM.CHK_DEST, only forced to paper check for
// everyone via "Override Direct Deposit". Oracle's :check_parms.head1 is just a dynamic label
// for the Landman No. field, not real data - dropped in favor of a plain label here.
const CheckRegisterGenerateDialog = (props: ICheckRegisterGenerateDialogProps) => {
    const { register, errors, getValues, setValue } = useForm();
    const { get } = useFetch();
    const [officeOptions, setOfficeOptions] = useState<Array<{ value: string; label: string }>>([]);

    useEffect(() => {
        const fetchOffices = async () => {
            const result = await get('/v1.0/Payroll/offices');
            if (Array.isArray(result)) {
                setOfficeOptions([{ value: 'ALL', label: 'All Offices' }, ...result.map((o: { code: string; dscr: string }) => ({ value: o.code, label: o.dscr }))]);
            }
        };
        fetchOffices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mirrors the main screen's own "Generate" button trigger, which auto-fills
    // :check_parms.check_no with the next real paper-check number before the dialog opens.
    // props.nextCheckNo is fetched by the parent at the moment "Generate" is clicked - this
    // dialog's own mount happens once at page load, long before the <input> that Dialog's
    // CSSTransition actually creates on open exists, so a fetch done here on mount would be
    // wiped the moment the real input mounts.
    useEffect(() => {
        if (props.nextCheckNo !== undefined) {
            setValue('beginningCheckNumber', props.nextCheckNo);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.nextCheckNo]);

    const handleOk = () => {
        if (props.onGenerate) {
            const values = getValues();
            props.onGenerate({
                periodNo: Number(values.periodNo),
                beginningCheckNumber: Number(values.beginningCheckNumber),
                checkDate: values.checkDate,
                type: values.type || 'C',
                tsdNoList: values.tsdNoList || undefined,
                office: values.office || 'ALL',
                overrideDirectDeposit: values.overrideDirectDeposit || 'N',
            });
        }
    };

    return (
        <Dialog title="Generate Checks" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '40%' }}>
            <RHFInputBox label="Pay Period Number" name="periodNo" type="number" register={register} errors={errors} labelOnLeft={true} spaceLabelContent={true} style={{ width: 'auto' }} />
            <RHFSelect label="Office" name="office" options={officeOptions} defaultVal="ALL" register={register} errors={errors} labelOnLeft={true} style={{ width: '100%' }} />
            <RHFInputBox
                label="Beginning Check Number"
                name="beginningCheckNumber"
                type="number"
                register={register}
                errors={errors}
                labelOnLeft={true}
                spaceLabelContent={true}
                style={{ width: 'auto' }}
            />
            <RHFInputBox label="Check Date" name="checkDate" type="date" register={register} errors={errors} labelOnLeft={true} spaceLabelContent={true} style={{ width: 'auto' }} />
            <RHFSelect
                label="Type"
                name="type"
                options={[
                    { value: 'C', label: 'Contractor' },
                    { value: 'E', label: 'Employee' },
                ]}
                defaultVal="C"
                register={register}
                errors={errors}
                labelOnLeft={true}
                style={{ width: '100%' }}
            />
            <RHFInputBox
                label="Landman No.(s)"
                name="tsdNoList"
                type="text"
                register={register}
                errors={errors}
                labelOnLeft={true}
                spaceLabelContent={true}
                style={{ width: '100%' }}
                title="Comma-separated. Blank = all eligible landmen"
            />
            <RHFSelect
                label="Override Direct Deposit"
                name="overrideDirectDeposit"
                options={[
                    { value: 'N', label: 'No' },
                    { value: 'Y', label: 'Yes' },
                ]}
                defaultVal="N"
                register={register}
                errors={errors}
                labelOnLeft={true}
                style={{ width: '100%' }}
            />
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button className="blue-hover-button" style={{ width: '20%', marginRight: '1rem' }} text="OK" onClick={handleOk} />
                <Button className="blue-hover-button" style={{ width: '20%' }} text="Cancel" onClick={() => props.hideDialog()} />
            </div>
            <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center', color: 'red' }}>{props.message}</div>
        </Dialog>
    );
};

export default CheckRegisterGenerateDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/checkRegister/CheckRegisterGenerateDialog.tsx